'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { c } from 'ui-components';
import { HorizontalFiltersSkeleton } from '../../Skeleton/HorizontalFiltersSkeleton';
import { PriceFacet } from '../PriceFacet';
import { PublisherFacet } from '../PublisherFacet';
import { RatingFacet } from '../RatingFacet';
import { ReleaseDateFacet } from '../ReleaseDateFacet';
import { ResultsPerPage } from '../ResultsPerPage';
import { Sort } from '../Sort';
import { useSearchLoading } from '../useSearchLoading';
import { HorizontalFiltersProps } from './HorizontalFilters.types';

export const HorizontalFilters = ({ className }: HorizontalFiltersProps) => {
  const { isInitialLoad } = useSearchLoading();
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  // The flat `search.filters` key is the only filters label in the CMS set.
  const filtersLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.filters',
    'Filters'
  );

  if (isInitialLoad) {
    return <HorizontalFiltersSkeleton className={className} />;
  }

  return (
    <div
      role="group"
      aria-label={filtersLabel}
      className={c('flex-wrap items-end justify-between gap-3', className)}
    >
      {/* Filter pills. All four stay visible from lg up: the retired CSR bar
          collapsed Publisher + Release date into an "All Filters" modal below
          xl, and this bar has no equivalent yet — an open horizontal-bar parity gap,
          not part of the mobile drawer (which carries every facet already).
          Until it lands, the four pills plus sort and page size exceed the lg
          main column, so the row wraps rather than overflowing it. */}
      <div className="flex items-center gap-2">
        <PriceFacet />
        <RatingFacet />
        <PublisherFacet />
        <ReleaseDateFacet />
      </div>

      {/* Sort by + Results per page (keep their own labels). `justify-between`
          on the wrapper, rather than `ml-auto` here, keeps this group right of
          the pills on a single row while leaving it flush left once the row
          wraps. */}
      <div className="flex items-end gap-3">
        <Sort />
        <ResultsPerPage />
      </div>
    </div>
  );
};
